import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Copy, FileDown, Loader2, Send, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { downloadDisputePdf } from "@/lib/disputePdf";
import { DisputeLessons } from "@/components/DisputeLessons";

const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const wk = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric" });

const Card = ({ label, value, id, hint }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p data-testid={id} className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</p>
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

const DisputeStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stripe-disputes/stats").then(({ data }) => setS(data)).catch(() => setS(false)); }, []);
  if (!s) return null;
  const y = s.year, a = s.all_time;
  return (
    <div data-testid="dispute-stats">
      <h3 className="mb-2 text-sm font-semibold text-slate-700">Disputes · last 52 weeks <span className="font-normal text-slate-400">(live payments only)</span></h3>
      {a.total === 0 ? <p data-testid="dispute-stats-empty" className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">No disputes on real payments yet.</p> : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Card label="Win rate" value={y.win_rate == null ? "—" : `${y.win_rate}%`} id="dispute-win-rate" hint={`All-time ${a.win_rate == null ? "—" : `${a.win_rate}%`}`} />
          <Card label="Won" value={y.won} id="dispute-won" hint={`All-time ${a.won}`} />
          <Card label="Lost" value={y.lost} id="dispute-lost" hint={`All-time ${a.lost} · ${money(a.lost_amount)}`} />
          <Card label="Money recovered" value={money(y.recovered)} id="dispute-recovered" hint={`All-time ${money(a.recovered)}`} />
          <Card label="Still open" value={y.open} id="dispute-open" hint={`${money(y.disputed_amount)} disputed this year`} />
        </div>
      )}
      {a.total > 0 && <DisputeLessons reasons={s.by_reason} />}
    </div>
  );
};

export const RevenuePanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/revenue", { params: { weeks: 52 } }).then(({ data: d }) => setData(d)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return <p className="grid place-items-center p-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></p>;
  const t = data.totals;
  const series = data.weeks.map((w) => ({ ...w, label: wk(w.week) }));
  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="revenue-panel">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Card label="Revenue · 52 weeks" value={money(t.revenue)} id="revenue-total" hint="CAD" />
        <Card label="Passes sold · 52 weeks" value={t.sales} id="revenue-sales" />
        <Card label="New sign-ups · 52 weeks" value={t.signups} id="revenue-signups" />
        <Card label="Active passes now" value={t.active_passes} id="revenue-active" />
        <Card label="All-time revenue" value={money(t.revenue_all_time)} id="revenue-all-time" hint={`${t.sales_all_time} pass${t.sales_all_time === 1 ? "" : "es"}`} />
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Weekly revenue (CAD)</h3>
        <div className="h-64" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={48} />
              <Tooltip formatter={(v, n) => (n === "Revenue" ? money(v) : v)} />
              <Bar dataKey="revenue" name="Revenue" fill="#4f46e5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Weekly passes sold and new sign-ups</h3>
        <div className="h-56" data-testid="signups-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={32} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="pass_1m" name="1-Month passes" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="pass_3m" name="3-Month passes" stroke="#4f46e5" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="signups" name="Sign-ups" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <DisputeStats />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm" data-testid="revenue-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Week of", "Revenue", "Passes", "1-Month", "3-Month", "Sign-ups"].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {[...series].reverse().filter((r) => r.sales || r.signups).map((r) => (
              <tr key={r.week} data-testid={`revenue-row-${r.week}`} className="text-slate-700"><td className="px-4 py-2 text-slate-700">{r.week}</td><td className="px-4 py-2 font-semibold text-slate-900">{money(r.revenue)}</td><td className="px-4 py-2">{r.sales}</td><td className="px-4 py-2">{r.pass_1m}</td><td className="px-4 py-2">{r.pass_3m}</td><td className="px-4 py-2">{r.signups}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ReplyBox = ({ msg, onSent }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 2) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/contacts/${msg.id}/reply`, { message: text }); toast.success(`Reply emailed to ${msg.email}`); setText(""); setOpen(false); onSent(data.reply); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2">
      {(msg.replies || []).map((r, i) => (
        <div key={i} data-testid={`contact-reply-${msg.id}-${i}`} className="wrap-anywhere rounded-lg border-l-4 border-indigo-400 bg-indigo-50 px-3 py-2 text-sm text-slate-700">
          <p className="text-xs font-semibold text-indigo-700">Reply by {r.by} · {new Date(r.sent_at).toLocaleString()}</p>
          <p className="whitespace-pre-line">{r.message}</p>
        </div>
      ))}
      {open ? (
        <div className="space-y-2">
          <textarea data-testid={`contact-reply-input-${msg.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={5000} autoFocus placeholder={`Write your reply to ${msg.name}…`}
            className="min-h-[110px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30" />
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid={`contact-reply-send-${msg.id}`} onClick={send} disabled={busy || text.trim().length < 2} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Send reply & resolve</button>
            <button data-testid={`contact-reply-cancel-${msg.id}`} onClick={() => setOpen(false)} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Cancel</button>
            <span className="text-xs text-slate-400">Sent from support@lexversant.ca</span>
          </div>
        </div>
      ) : <button data-testid={`contact-reply-open-${msg.id}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 px-4 py-1.5 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-50"><Send className="h-3.5 w-3.5" />Reply by email</button>}
    </div>
  );
};

export const EvidenceBtn = ({ userId, sessionId = "", id }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("fraudulent");
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/disputes/${userId}/explanation`, { reason, session_id: sessionId });
      downloadDisputePdf(data.evidence); setText(data.explanation); setNotes(data.reviewer_notes || ""); toast.success("Evidence PDF downloaded and explanation ready");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pdfOnly = async () => {
    try { const { data } = await api.get(`/admin/disputes/${userId}`); downloadDisputePdf(data); toast.success("Dispute evidence downloaded"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied — paste it into Stripe"); } catch { toast.error("Copy failed. Select the text and copy it manually."); } };
  return (
    <>
      <button data-testid={id} onClick={() => { setText(""); setOpen(true); }} title="Evidence PDF and Stripe explanation"
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 transition-colors hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700">
        <FileDown className="h-3.5 w-3.5" />Dispute kit
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="dispute-kit-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stripe dispute kit</DialogTitle>
            <DialogDescription>Pick the reason shown in Stripe. We download the evidence PDF and write an explanation from this student's records. Review it before submitting.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2" data-testid="dispute-reason-group">
            {REASONS.map(([k, l]) => (
              <button key={k} type="button" data-testid={`dispute-reason-${k}`} onClick={() => setReason(k)}
                className={`rounded-xl border px-3 py-2 text-left text-xs font-semibold transition-colors ${reason === k ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="dispute-prepare-btn" onClick={prepare} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{busy ? "Writing…" : "Prepare PDF + explanation"}</button>
            <button data-testid="dispute-pdf-only-btn" onClick={pdfOnly} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"><FileDown className="h-4 w-4" />PDF only</button>
          </div>
          {text && (
            <div className="space-y-2">
              {notes && <p data-testid="dispute-reviewer-notes" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"><b>For you only (don't paste):</b> {notes}</p>}
              <textarea data-testid="dispute-explanation-text" value={text} onChange={(e) => setText(e.target.value)} className="min-h-[260px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm leading-relaxed text-slate-900 outline-none focus:border-indigo-500" />
              <button data-testid="dispute-copy-btn" onClick={copy} className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-slate-700"><Copy className="h-4 w-4" />Copy explanation</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
const REASONS = [["fraudulent", "Fraudulent / not authorized"], ["product_not_received", "Product not received"], ["not_as_described", "Not as described / unsatisfied"], ["duplicate", "Duplicate charge"]];
